import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Delaunay } from 'd3-delaunay';

const VORONOI_PALETTE = [
  '#FCD34D', '#FDE68A', '#3b82f6', '#10b981', '#f59e0b',
  '#ef4444', '#06b6d4', '#FDE047', '#14b8a6', '#d946ef',
  '#FCD34D', '#F59E0B', '#84cc16', '#0ea5e9', '#a855f7',
];

const VoronoiLayer = ({ results, categoryColorMap, onMarkerClick, onMarkerHover, onMarkerLeave }) => {
  const map = useMap();
  const layerGroupRef = useRef(null);
  const markersRef = useRef(null);

  const buildVoronoi = useCallback(() => {
    // Clean up previous
    if (layerGroupRef.current) {
      map.removeLayer(layerGroupRef.current);
      layerGroupRef.current = null;
    }
    if (markersRef.current) {
      map.removeLayer(markersRef.current);
      markersRef.current = null;
    }

    if (!results || results.length < 3) return;

    // Filter valid points
    const validResults = results.filter(
      (r) => r.latitude && r.longitude && r.latitude >= -85 && r.latitude <= 85 && r.longitude >= -180 && r.longitude <= 180
    );
    if (validResults.length < 3) return;

    // Deduplicate close points (within ~0.1 degree) to avoid degenerate Voronoi
    const deduped = [];
    const seen = new Set();
    for (const r of validResults) {
      const key = `${Math.round(r.latitude * 10)},${Math.round(r.longitude * 10)}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(r);
      }
    }
    if (deduped.length < 3) return;

    // Project lat/lng to pixel space for Voronoi computation
    const bounds = map.getBounds();
    const nw = map.latLngToLayerPoint(bounds.getNorthWest());
    const se = map.latLngToLayerPoint(bounds.getSouthEast());
    const width = se.x - nw.x;
    const height = se.y - nw.y;

    const points = deduped.map((r) => {
      const pt = map.latLngToLayerPoint([r.latitude, r.longitude]);
      return [pt.x - nw.x, pt.y - nw.y];
    });

    // Compute Voronoi using d3-delaunay
    const delaunay = Delaunay.from(points);
    const voronoi = delaunay.voronoi([0, 0, width, height]);

    const polyGroup = L.layerGroup();

    for (let i = 0; i < deduped.length; i++) {
      const cell = voronoi.cellPolygon(i);
      if (!cell) continue;

      // Convert pixel coords back to lat/lng
      const latLngs = cell.map(([px, py]) => {
        return map.layerPointToLatLng([px + nw.x, py + nw.y]);
      });

      // Determine color from category or fallback
      const r = deduped[i];
      const cat = r.categories?.[0];
      let color = cat && categoryColorMap[cat]
        ? categoryColorMap[cat]
        : VORONOI_PALETTE[i % VORONOI_PALETTE.length];

      const polygon = L.polygon(latLngs, {
        color: color,
        weight: 1.5,
        opacity: 0.7,
        fillColor: color,
        fillOpacity: 0.12,
        smoothFactor: 1,
        className: 'voronoi-cell',
      });

      // Hover effect
      polygon.on('mouseover', function () {
        this.setStyle({ fillOpacity: 0.35, weight: 2.5, opacity: 1 });
      });
      polygon.on('mouseout', function () {
        this.setStyle({ fillOpacity: 0.12, weight: 1.5, opacity: 0.7 });
      });
      polygon.on('click', () => onMarkerClick(r));

      polyGroup.addLayer(polygon);
    }

    polyGroup.addTo(map);
    layerGroupRef.current = polyGroup;

    // Add small dot markers at each point
    const dotGroup = L.layerGroup();
    deduped.forEach((r) => {
      const cat = r.categories?.[0];
      const color = cat && categoryColorMap[cat]
        ? categoryColorMap[cat]
        : '#047857';

      const marker = L.circleMarker([r.latitude, r.longitude], {
        radius: 4,
        fillColor: color,
        fillOpacity: 0.9,
        color: '#0F172A',
        weight: 1.5,
        resultData: r,
      });
      marker.on('click', () => onMarkerClick(r));
      marker.on('mouseover', (e) => onMarkerHover(r, e));
      marker.on('mouseout', () => onMarkerLeave());
      dotGroup.addLayer(marker);
    });

    dotGroup.addTo(map);
    markersRef.current = dotGroup;
  }, [results, categoryColorMap, map, onMarkerClick, onMarkerHover, onMarkerLeave]);

  useEffect(() => {
    buildVoronoi();

    // Rebuild on zoom/move since Voronoi depends on projection
    const rebuild = () => buildVoronoi();
    map.on('moveend', rebuild);

    return () => {
      map.off('moveend', rebuild);
      if (layerGroupRef.current) {
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current = null;
      }
      if (markersRef.current) {
        map.removeLayer(markersRef.current);
        markersRef.current = null;
      }
    };
  }, [buildVoronoi]);

  return null;
};

export default VoronoiLayer;
